import React, { useEffect, useState } from 'react';
import { BsCamera, BsEye, BsEyeSlash } from 'react-icons/bs';
import api from '../../services/Api';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const CreateAdmin = ({ editMode = false, adminData = null }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    nationality: '',
    email: '',
    accessRole: '',
    phone: '',
    username: '',
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [adminDetails, setAdminDetails] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUpdateOption, setPasswordUpdateOption] = useState(null);
  const userData = useSelector(state => state.auth.userData);
  const currentAdminId = userData?.sub;
  const userEmail = userData?.email; 

  useEffect(() => {
    const fetchAdminDetails = async adminId => {
      try {
        setIsLoading(true);
        const response = await api.get(`/admin/${adminId}`);
        if (response.data && response.data.admin) {
          setAdminDetails(response.data.admin);
        }
      } catch (error) {
        console.error('Error fetching admin details:', error);
        setMessage({ type: 'error', text: 'Failed to load admin details' });
      } finally {
        setIsLoading(false);
      }
    };

    if (editMode && !adminData && currentAdminId) {
      fetchAdminDetails(currentAdminId);
    }
  }, [editMode, adminData, currentAdminId]);

  useEffect(() => {
    const admin = adminData || adminDetails;

    if (editMode && admin) {
      setFormData({
        fullName: admin?.admin?.fullName || admin?.fullName || '',
        nationality: admin?.admin?.nationality || admin?.nationality || '',
        email: admin?.admin?.email || admin?.email || '',
        accessRole: admin?.admin?.role?.toLowerCase() || admin?.role?.toLowerCase() || '',
        phone: admin?.admin?.phone || admin?.phone || '',
        username: admin?.admin?.username || admin?.username || '',
      });

      if (admin?.admin?.image) {
        setImageUrl(admin.admin.image);
      } else if (admin?.image) {
        setImageUrl(admin.image);
      }
    }
  }, [editMode, adminData, adminDetails]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordInputChange = e => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    
    if (file) {
      const maxSize = 5 * 1024 * 1024; 
      
      if (file.size > maxSize) {
        const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        alert(`File size (${sizeInMB}MB) exceeds 5MB limit. Please choose a smaller image.`);
        e.target.value = ""; 
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPG, PNG, and GIF images are allowed.');
        e.target.value = "";
        return;
      }

      setProfilePhoto(file);
      const objectUrl = URL.createObjectURL(file);
      setImageUrl(objectUrl);
    }
  };

  const uploadPhoto = async () => {
    if (!profilePhoto) return null;

    const formData = new FormData();
    formData.append('file', profilePhoto);

    try {
      const response = await api.post(`/upload/file/${userEmail}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.url) {
        return response.data.url;
      }
      return null;
    } catch (error) {
      console.error('Error uploading photo:', error);
      return null;
    }
  };

  const prepareAdminPayload = (uploadedPhotoUrl, includePassword = false) => {
    const payload = {
      fullName: formData.fullName,
      nationality: formData.nationality,
      email: formData.email,
      role: formData.accessRole.toUpperCase(),
      phone: formData.phone,
      username: formData.username,
    };

    if (uploadedPhotoUrl || imageUrl) {
      payload.image = uploadedPhotoUrl || imageUrl;
    }
    if (includePassword && passwordData.newPassword) {
      payload.password = passwordData.newPassword;
      payload.confirmPassword = passwordData.confirmPassword;
    }

    if (editMode) {
      const admin = adminData || adminDetails;
      payload._id = admin?._id || currentAdminId;
    }

    return payload;
  };

  const createOrUpdateAdmin = async (uploadedPhotoUrl, includePassword = false) => {
    try {
      const adminPayload = prepareAdminPayload(uploadedPhotoUrl, includePassword);

      const endpoint = editMode ? '/admin/update' : '/admin/register';
      const response = await api.post(endpoint, adminPayload);

      if (response.data) {
        setMessage({
          type: 'success',
          text: editMode ? 'Admin updated successfully!' : 'Admin created successfully!',
        });

        if (!editMode) {
          setFormData({
            fullName: '',
            nationality: '',
            email: '',
            accessRole: '',
            phone: '',
            username: '',
          });
          setPasswordData({
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
          setImageUrl('');
          setProfilePhoto(null);
        } else {
          closePasswordModal();
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('API Error:', error.response?.data || error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || `Error ${editMode ? 'updating' : 'creating'} admin`,
      });
      return false;
    }
  };

  const openPasswordModal = () => {
    setShowPasswordModal(true);
    setPasswordUpdateOption(null);
    setPasswordData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordUpdateOption(null);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!editMode) {
      await handleCreateAdmin(e);
    } else {
      openPasswordModal();
    }
  };

  const handleCreateAdmin = async e => {
    if (e) e.preventDefault();
    setIsLoading(true);

    try {
      if (
        !editMode &&
        (!passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword)
      ) {
        setMessage({ type: 'error', text: 'Valid password is required for new admin' });
        setIsLoading(false);
        return;
      }

      let uploadedPhotoUrl = null;
      if (profilePhoto) {
        uploadedPhotoUrl = await uploadPhoto();
      }

      await createOrUpdateAdmin(uploadedPhotoUrl, true);
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateWithPassword = async e => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (passwordData.newPassword && passwordData.newPassword !== passwordData.confirmPassword) {
        setMessage({ type: 'error', text: 'Passwords do not match!' });
        setIsLoading(false);
        return;
      }

      let uploadedPhotoUrl = null;
      if (profilePhoto) {
        uploadedPhotoUrl = await uploadPhoto();
      }

      const success = await createOrUpdateAdmin(uploadedPhotoUrl, true);
      if (success) {
        closePasswordModal();
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateWithoutPassword = async () => {
    setIsLoading(true);
    try {
      let uploadedPhotoUrl = null;
      if (profilePhoto) {
        uploadedPhotoUrl = await uploadPhoto();
      }

      const adminPayload = prepareAdminPayload(uploadedPhotoUrl, false);

      if (passwordData.oldPassword) {
        adminPayload.password = passwordData.oldPassword;
        adminPayload.confirmPassword = passwordData.oldPassword;
      }
      const endpoint = '/admin/update';
      const response = await api.post(endpoint, adminPayload);

      if (response.data) {
        setMessage({
          type: 'success',
          text: 'Admin updated successfully!',
        });
        closePasswordModal();
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'An error occurred during update',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message.text]);

  const handleCancel = () => {
    navigate('/admin-management');
  };

  if (editMode && isLoading && !adminDetails && !adminData) {
    return (
      <div className="p-4 sm:ml-64 h-screen w-[80%] bg-[#F4F5F6] font-nohemi flex items-center justify-center">
        <div className="text-lg">Loading admin details...</div>
      </div>
    );
  }

  const PasswordModal = () => {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 rounded-xl w-11/12 md:w-2/3 lg:w-1/2 max-h-[90vh] overflow-y-auto">
          <h2 className="mb-4 text-lg font-bold">Confirm Admin Details</h2>
          {/* Display admin information */}
          <div className="p-4 mb-6 border rounded bg-gray-50">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="col-span-2 mx-auto">
                {imageUrl && (
                  <div className="mt-2">
                    <div className="w-20 h-20 mt-1 overflow-hidden rounded-full">
                      <img src={imageUrl} alt="Profile" className="object-cover w-full h-full" />
                    </div>
                  </div>
                )}
              </div>
              <div className="m-2 font-light text-gray-500">
                <span className="px-2 font-bold text-black">Full Name:</span> {formData.fullName}
              </div>
              <div className="m-2 font-light text-gray-500">
                <span className="px-2 font-bold text-black">Email:</span> {formData.email}
              </div>
              <div className="m-2 font-light text-gray-500">
                <span className="px-2 font-bold text-black">Username:</span> {formData.username}
              </div>
              <div className="m-2 font-light text-gray-500">
                <span className="px-2 font-bold text-black">Role:</span>{' '}
                {formData.accessRole.toUpperCase()}
              </div>
              <div className="m-2 font-light text-gray-500">
                <span className="px-2 font-bold text-black">Phone:</span>{' '}
                {formData.phone || 'N/A'}
              </div>
              <div className="m-2 font-light text-gray-500">
                <span className="px-2 font-bold text-black">Nationality:</span>{' '}
                {formData.nationality || 'N/A'}
              </div>
            </div>
          </div>

          {passwordUpdateOption === null && (
            <>
              <p className="mb-4 text-sm text-gray-600">
                Do you want to update the password for this admin?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setPasswordUpdateOption('skip')}
                  className="flex-1 px-4 py-2 text-sm text-black bg-gray-200 rounded-lg">
                  Skip Password Update
                </button>
                <button
                  type="button"
                  onClick={() => setPasswordUpdateOption('update')}
                  className="flex-1 px-4 py-2 text-sm text-white bg-black rounded-lg">
                  Update With Password
                </button>
              </div>
            </>
          )}

          {passwordUpdateOption === 'skip' && (
            <div className="mt-4">
              <div className="mb-4">
                <label
                  htmlFor="oldPassword"
                  className="block mb-2 text-sm font-bold text-gray-600">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="oldPassword"
                    name="oldPassword"
                    value={passwordData.oldPassword}
                    onChange={handlePasswordInputChange}
                    className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5 pr-10"
                    placeholder="Enter current password"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute z-10 flex items-center transform -translate-y-1/2 cursor-pointer top-1/2 right-2">
                    {showPassword ? (
                      <BsEyeSlash className="w-5 h-5 text-gray-500" />
                    ) : (
                      <BsEye className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-between gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setPasswordUpdateOption(null)}
                  className="flex-1 px-4 py-2 text-sm text-black bg-gray-200 rounded-lg">
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleUpdateWithoutPassword}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 text-sm text-white bg-black rounded-lg disabled:bg-gray-400">
                  {isLoading ? 'Updating...' : 'Update Admin'}
                </button>
              </div>
            </div>
          )}

          {passwordUpdateOption === 'update' && (
            <form className="mt-4" onSubmit={handleUpdateWithPassword}>
              <div className="mb-4">
                <label
                  htmlFor="oldPassword"
                  className="block mb-2 text-sm font-bold text-gray-600">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="oldPassword"
                    name="oldPassword"
                    value={passwordData.oldPassword}
                    onChange={handlePasswordInputChange}
                    className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                    placeholder="Current Password"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer">
                    {showPassword ? (
                      <BsEyeSlash className="w-5 h-5 text-gray-500" />
                    ) : (
                      <BsEye className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="newPassword"
                  className="block mb-2 text-sm font-bold text-gray-600">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordInputChange}
                    className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                    placeholder="New Password"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer">
                    {showPassword ? (
                      <BsEyeSlash className="w-5 h-5 text-gray-500" />
                    ) : (
                      <BsEye className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="confirmPassword"
                  className="block mb-2 text-sm font-bold text-gray-600">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordInputChange}
                    className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                    placeholder="Confirm New Password"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer">
                    {showPassword ? (
                      <BsEyeSlash className="w-5 h-5 text-gray-500" />
                    ) : (
                      <BsEye className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex justify-between gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setPasswordUpdateOption(null)}
                  className="flex-1 px-4 py-2 text-sm text-black bg-gray-200 rounded-lg">
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 text-sm text-white bg-black rounded-lg disabled:bg-gray-400">
                  {isLoading ? 'Updating...' : 'Update Admin'}
                </button>
              </div>
            </form>
          )}

          {message.text && (
            <div
              className={`text-sm ${
                message.type === 'error' ? 'text-red-500' : 'text-green-500'
              } text-center mt-4`}>
              {message.text}
            </div>
          )}

          {passwordUpdateOption === null && (
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={closePasswordModal}
                className="px-4 py-2 text-sm text-black bg-gray-200 rounded-lg">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:ml-64 h-screen w-[80%] bg-[#F4F5F6] font-nohemi">
      <div className="mb-2">
        <p className="text-xl font-semibold text-gray-900">
          {editMode ? 'Edit Admin' : 'Create New Admin'}
        </p>
      </div>
      <div className="p-6 bg-white rounded-xl">
        {message.text && (
          <div
            className={`text-sm ${
              message.type === 'error' ? 'text-red-500' : 'text-green-500'
            } text-center mb-4`}>
            {message.text}
          </div>
        )}
        <div className="flex flex-col items-center justify-center">
          <form className="w-3/5 h-screen" onSubmit={handleSubmit}>
            <div className="flex flex-col items-center justify-center p-4 m-2">
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex items-center justify-center overflow-hidden bg-gray-200 rounded-full w-16 h-16">
                  {imageUrl ? (
                    <img src={imageUrl} alt="Profile" className="object-cover w-full h-full" />
                  ) : (
                    <BsCamera className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </label>
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleFileChange}
                accept="image/*"
              />
              <p className="mt-4 text-sm">Upload Photo</p>
            </div>

            <div className="grid gap-8 mb-6 md:grid-cols-2">
              <div>
                <label htmlFor="fullName" className="block mb-2 text-base font-bold text-gray-600">
                  Full Name
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                  placeholder="Full Name"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="nationality"
                  className="block mb-2 text-base font-bold text-gray-600">
                  Nationality
                </label>
                <input
                  type="text"
                  id="nationality"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                  placeholder="Nationality"
                />
              </div>

              <div>
                <label htmlFor="email" className="block mb-2 text-base font-bold text-gray-600">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                  placeholder="Email"
                  required
                />
              </div>

              <div>
                <label htmlFor="phone" className="block mb-2 text-base font-bold text-gray-600">
                  Phone Number
                </label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                  placeholder="Phone Number"
                />
              </div>

              <div>
                <label htmlFor="username" className="block mb-2 text-base font-bold text-gray-600">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                  placeholder="Username"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="accessRole"
                  className="block mb-2 text-base font-bold text-gray-600">
                  Assign Role
                </label>
                <select
                  id="accessRole"
                  name="accessRole"
                  value={formData.accessRole}
                  onChange={handleInputChange}
                  className="w-full p-2 text-sm font-bold text-gray-600 border rounded-lg bg-gray-50"
                  required>
                  <option value="">Select Access Role</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </div>

              {!editMode && (
                <>
                  <div>
                    <label
                      htmlFor="newPassword"
                      className="block mb-2 text-base font-bold text-gray-600">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordInputChange}
                        className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                        placeholder="Password"
                        required
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer">
                        {showPassword ? (
                          <BsEyeSlash className="w-5 h-5 text-gray-500" />
                        ) : (
                          <BsEye className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block mb-2 text-base font-bold text-gray-600">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordInputChange}
                        className="bg-gray-50 border text-gray-900 text-sm rounded-lg focus:ring-1 focus:ring-gray-200 block w-full p-2.5"
                        placeholder="Confirm Password"
                        required
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer">
                        {showPassword ? (
                          <BsEyeSlash className="w-5 h-5 text-gray-500" />
                        ) : (
                          <BsEye className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-between w-full">
              <div className="w-[35%]">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-full text-black bg-white border focus:ring-0 focus:outline-none font-normal rounded-lg text-base py-2.5 mt-4 text-center">
                  Cancel
                </button>
              </div>
              <div className="w-[35%]">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full text-white bg-black hover:bg-gray-800 focus:ring-0 focus:outline-none font-normal rounded-lg text-base py-2.5 mt-4 text-center disabled:bg-gray-400">
                  {isLoading ? 'Saving...' : editMode ? 'Update' : 'Create Admin'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {showPasswordModal && <PasswordModal />}
    </div>
  );
};

export default CreateAdmin;
